import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Masonry from 'react-masonry-css';
import {
    IconPrinter, IconArrowUp, IconAdjustmentsHorizontal, IconRefresh, IconSearch, IconX,
    IconArrowsSort, IconClock, IconSortAscendingLetters, IconChevronDown, IconCalendar,
    IconLoader2, IconSortAscending, IconSortDescending,
    IconHeart, IconHeartFilled, IconTrendingUp, IconMessage2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import printTemplateService from '../../../services/printTemplateService';
import printTemplateCategoryService from '../../../services/printTemplateCategoryService';
import { usePrintTemplateFavoritesStore } from '../../../store/printTemplateFavoritesStore';
import PrintFeedbackModal from './PrintFeedbackModal';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

const PAGE_SIZE = 18;
const masonryBreakpoints = { default: 5, 1920: 5, 1440: 4, 1280: 3, 1024: 2, 640: 1 };
const CARD_FOOTER_H = 0.32; // footer + margin, relative to column width

// Date-range filter → epoch-ms cutoff. Templates with createdAt >= cutoff pass.
// 'today' = since local midnight; the rest are rolling windows from now.
function dateRangeCutoff(range) {
    const now = new Date();
    if (range === 'today') {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }
    const days = { '3days': 3, '7days': 7, '30days': 30 }[range];
    if (!days) return null;
    return now.getTime() - days * 24 * 60 * 60 * 1000;
}

/** Card height estimate for the masonry (height/width), from the page size. */
function aspectOf(tpl) {
    const w = Number(tpl.width) || 1050;
    const h = Number(tpl.height) || 600;
    return h / w;
}

/**
 * Greedy balanced masonry — each card goes to the currently-shortest column so
 * columns stay even, since a fixed-row grid either crops these thumbnails
 * (wide banners to tall posters) or leaves holes under the short ones.
 *
 * `sequential` switches that off for round-robin placement, because balanced
 * packing puts cards wherever they fit — which would scramble the reading order
 * of the shelf an admin arranged by hand.
 */
function BalancedMasonry({ items, columnCount, renderItem, heightOf, sequential = false }) {
    const columns = useMemo(() => {
        const cols = Array.from({ length: columnCount }, () => []);
        if (sequential) {
            items.forEach((it, i) => cols[i % columnCount].push(it));
            return cols;
        }
        const heights = new Array(columnCount).fill(0);
        for (const it of items) {
            const est = heightOf(it);
            let min = 0;
            for (let i = 1; i < columnCount; i++) if (heights[i] < heights[min]) min = i;
            cols[min].push(it);
            heights[min] += est;
        }
        return cols;
    }, [items, columnCount, heightOf, sequential]);

    return (
        <div className="flex w-auto -ml-4">
            {columns.map((col, i) => (
                <div key={i} className="pl-4 bg-clip-padding flex-1 min-w-0">
                    {col.map(renderItem)}
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Print-media browser — masonry grid + sticky filter sidebar
// ---------------------------------------------------------------------------
export default function PrintMediaBrowser() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const fmt = (n) => (n ?? 0).toLocaleString(isBn ? 'bn-BD' : 'en-US');

    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Per-user hearts — their own store, so they stay fresh independently of the
    // template list.
    const favSet = usePrintTemplateFavoritesStore((st) => st.favorites);
    const toggleFavorite = usePrintTemplateFavoritesStore((st) => st.toggle);
    const loadFavorites = usePrintTemplateFavoritesStore((st) => st.load);
    useEffect(() => { loadFavorites(); }, [loadFavorites]);

    // ── Column count from the ACTUAL grid width (viewport minus the sidebar),
    //    targeting ~250px per card, so wider screens pack more per row. ──
    const gridRef = useRef(null);
    const loadTokenRef = useRef(0); // a newer search supersedes a slower, older load
    const [gridWidth, setGridWidth] = useState(0);
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const measure = () => setGridWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const columnCount = gridWidth ? Math.max(1, Math.min(6, Math.floor(gridWidth / 250))) : 3;

    // Measured thumbnail ratios (height/width) by id, so the masonry balances
    // from real heights once the images land rather than the page-size estimate.
    const [ratios, setRatios] = useState({});
    const reportRatio = useCallback((id, r) => {
        if (!r || !isFinite(r)) return;
        setRatios((prev) => (prev[id] ? prev : { ...prev, [id]: r }));
    }, []);
    const heightOf = useCallback(
        (it) => (ratios[it.id] ?? aspectOf(it)) + CARD_FOOTER_H,
        [ratios],
    );

    // ── Filter state ──
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
    const [pricing, setPricing] = useState('all');
    // 'order' is the shelf the admin arranged — the browser's own default view.
    const [sort, setSort] = useState('order');
    // Direction toggle: 'desc' is each sort's natural order (default), 'asc'
    // reverses it — including the featured shelf, which is then read bottom-up.
    const [sortDir, setSortDir] = useState('desc');
    const [dateRange, setDateRange] = useState('all');
    const [favOnly, setFavOnly] = useState(false); // ❤️ header toggle
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    // The prompt only shows once the user has actually run out of templates to
    // look at, and stays gone for the session once dismissed.
    const [atPageEnd, setAtPageEnd] = useState(false);
    // Templates and categories are two separate requests; the grid is not its
    // real size until BOTH have landed (see the check below).
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [pillDismissed, setPillDismissed] = useState(false);
    const [pillShaking, setPillShaking] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    useEffect(() => {
        setActiveCategory(searchParams.get('category') || 'all');
    }, [searchParams]);

    /**
     * The prompt shakes a second after it settles, then keeps nudging every
     * 1.5s while it is on screen. The first delay lets the slide-in finish, so
     * the two movements read as separate rather than one long wobble.
     *
     * Each shake turns the animation off and back on across a frame — restarting
     * a CSS animation needs it to actually stop first, or the second trigger is
     * a no-op while the first is still running.
     */
    const pillVisible = atPageEnd && !pillDismissed && !feedbackOpen;
    useEffect(() => {
        if (!pillVisible) { setPillShaking(false); return undefined; }

        let interval;
        const shake = () => {
            setPillShaking(false);
            requestAnimationFrame(() => setPillShaking(true));
        };
        const first = setTimeout(() => {
            shake();
            interval = setInterval(shake, 1500);
        }, 1000);

        return () => {
            clearTimeout(first);
            clearInterval(interval);
        };
    }, [pillVisible]);

    const handleCategoryChange = (cat) => {
        // Picking a category is a fresh browse — clear the other filters.
        setSearchQuery('');
        setPricing('all');
        setDateRange('all');
        if (cat === 'all') setSearchParams({});
        else setSearchParams({ category: cat });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: 'active' };
            if (debouncedSearch) params.search = debouncedSearch.trim();
            const token = ++loadTokenRef.current;
            // The grid renders the first page at once; the rest streams in behind it.
            const res = await printTemplateService.listAll(params, (data) => {
                if (token !== loadTokenRef.current) return;
                setTemplates(data);
                setLoading(false);
            });
            if (token === loadTokenRef.current) setTemplates(res?.data || []);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('printMedia.failedToLoadTemplates'),
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        // Only published categories reach the browser — the endpoint already
        // filters for non-admins, but a template in a drafted category would
        // otherwise still show under "All".
        printTemplateCategoryService.list()
            .then(r => setCategories((r?.data || []).filter(c => c.status === 'published')))
            .catch(() => {})
            .finally(() => setCategoriesLoaded(true));
    }, []);

    // ── Derived ──
    // The reading language picks first, but each side falls back to the other:
    // a category with only one of the two names must never render as a blank row.
    const getCatName = (cat) => (
        (isBn ? (cat?.nameBn || cat?.name) : (cat?.name || cat?.nameBn)) || ''
    );
    const publishedIds = useMemo(() => new Set(categories.map(c => c.id)), [categories]);

    // A template in a drafted (or deleted) category is not browsable — it would
    // otherwise sit under "All" with no category anyone can navigate to.
    const visibleUniverse = useMemo(
        () => templates.filter(tpl => tpl.categoryId == null || publishedIds.has(tpl.categoryId)),
        [templates, publishedIds],
    );

    const activeCategoryObj = activeCategory === 'all'
        ? null
        : categories.find(c => c.slug === activeCategory) || null;
    const activeCategoryId = activeCategoryObj?.id ?? null;
    const pinnedCategory = useMemo(() => categories.find(c => c.pinned) || null, [categories]);

    const countByCategory = useMemo(() => {
        const map = new Map();
        visibleUniverse.forEach((tpl) => {
            if (tpl.categoryId == null) return;
            map.set(tpl.categoryId, (map.get(tpl.categoryId) || 0) + 1);
        });
        return { map };
    }, [visibleUniverse]);

    // Category marquee: keep the SPEED constant (px/sec) regardless of how many
    // categories there are. The row is duplicated for a seamless loop, so it
    // travels one set-width per cycle; duration = that width ÷ speed. Without
    // this a fixed duration races on a long list and crawls on a short one.
    const MARQUEE_SPEED = 22;
    const marqueeRowRef = useRef(null);
    const [marqueeDuration, setMarqueeDuration] = useState(60);
    const [marqueePaused, setMarqueePaused] = useState(false);
    useEffect(() => {
        const el = marqueeRowRef.current;
        if (!el || categories.length === 0) return;
        const measure = () => {
            const oneSetWidth = el.scrollWidth / 2;
            if (oneSetWidth > 0) setMarqueeDuration(oneSetWidth / MARQUEE_SPEED);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [categories]);

    // ── Apply filters + sort ──
    const filtered = useMemo(() => {
        let list = visibleUniverse;
        if (activeCategoryId != null) list = list.filter(tpl => tpl.categoryId === activeCategoryId);
        if (pricing !== 'all') list = list.filter(tpl => (pricing === 'free' ? tpl.isFree === true : tpl.isFree !== true));
        if (dateRange !== 'all') {
            const cutoff = dateRangeCutoff(dateRange);
            if (cutoff != null) list = list.filter(tpl => new Date(tpl.createdAt || 0).getTime() >= cutoff);
        }

        // Each sort produces its natural order first — the admin's arrangement as
        // the server served it, newest first, oldest first, A→Z — and the
        // direction toggle then reverses whichever one is active. Reversing at the
        // end rather than inside each comparator is what lets the toggle mean
        // something under EVERY sort, including the hand-arranged shelf that has
        // no comparator of its own.
        const sorted = [...list];
        if (sort === 'popular') sorted.sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0) || b.id - a.id);
        else if (sort === 'newest') sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0) || b.id - a.id);
        else if (sort === 'oldest') sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0) || a.id - b.id);
        else if (sort === 'name') sorted.sort((a, b) => String((isBn && a.titleBn) || a.title || '').localeCompare(String((isBn && b.titleBn) || b.title || ''), i18n.language));
        return sortDir === 'asc' ? sorted.reverse() : sorted;
    }, [visibleUniverse, activeCategory, activeCategoryId, pricing, sort, sortDir, dateRange, isBn, i18n.language]);

    // Reset paging whenever the result set changes shape.
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, pricing, sort, sortDir, dateRange, debouncedSearch, favOnly]);

    const filtersDirty = activeCategory !== 'all' || pricing !== 'all' || sort !== 'order'
        || sortDir !== 'desc' || dateRange !== 'all' || !!searchQuery;

    // In the untouched default view the admin-pinned category floats to the
    // front of the one flat grid; any filter or sort the user picks turns that
    // off and the grid is exactly what they asked for.
    const ordered = useMemo(() => {
        // ❤️ on → only the hearted ones, and the pinned category stops mattering.
        if (favOnly) return filtered.filter((tpl) => favSet.has(tpl.id));
        if (filtersDirty || pinnedCategory == null) return filtered;
        const rank = (tpl) => (tpl.categoryId === pinnedCategory.id ? 1 : 0);
        return [...filtered].sort((a, b) => rank(b) - rank(a));
    }, [filtered, filtersDirty, pinnedCategory, favOnly, favSet]);

    const shown = ordered.slice(0, visibleCount);
    const hasMore = ordered.length > visibleCount;

    /**
     * "Has the user scrolled to the end of the page?" — measured from the
     * document rather than an element, so it works the same on every filter and
     * category view, and on both layouts (mobile has no sidebar holding the page
     * open, so its pages are much shorter for the same result set).
     *
     * Re-checked on resize too: rotating a phone or opening the keyboard changes
     * the viewport height, and the answer with it.
     */
    useEffect(() => {
        const check = () => {
            // Nothing counts until the grid is showing its real contents.
            //
            // `loading` alone is not enough: categories arrive in a SECOND
            // request, and until they do `visibleUniverse` filters almost every
            // template out — so the page is briefly short, "at the end" is
            // trivially true, and the prompt flashes up before the grid fills in.
            if (loading || !categoriesLoaded) { setAtPageEnd(false); return; }
            const viewport = window.innerHeight;
            const full = document.documentElement.scrollHeight;
            // The page has to be long enough to scroll before reaching its end
            // means anything. Without this a short result set — one template in a
            // category, which on mobile has no sidebar to pad the page out — is
            // "already at the end" the moment it loads, and the prompt appears
            // without the user having scrolled at all.
            const scrollable = full > viewport + 24;
            setAtPageEnd(scrollable && window.scrollY + viewport >= full - 120);
        };
        check();
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        // Cards and thumbnails land after the first paint and make the page
        // taller without firing scroll or resize — so watch the document's own
        // height rather than waiting for an event that never comes.
        const ro = new ResizeObserver(check);
        ro.observe(document.documentElement);
        return () => {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            ro.disconnect();
        };
    }, [loading, categoriesLoaded, shown.length]);

    // Infinite scroll: reveal the next page when the sentinel scrolls into view.
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore) return;
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0]?.isIntersecting) setVisibleCount(c => c + PAGE_SIZE); },
            { rootMargin: '400px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, shown.length]);

    const resetFilters = () => {
        handleCategoryChange('all');
        setPricing('all');
        setSort('order');
        setSortDir('desc');
        setDateRange('all');
        setSearchQuery('');
        setFavOnly(false);
    };

    // Filter controls are shared between the desktop sidebar and the mobile
    // drawer (Sheet), so they render identically in both places.
    const filterHeaderRow = (
        <div className="flex items-center gap-2 mb-4">
            <IconAdjustmentsHorizontal size={17} className="text-[#E2702E]" />
            <span className="text-sm font-bold text-[#1C1B19] dark:text-zinc-100">{t('worldCupJersey.filters')}</span>
            <button
                onClick={resetFilters}
                disabled={!filtersDirty}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold transition-colors text-[#E2702E] hover:text-[#E2702E]/80 disabled:text-[#C3BEB4] dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
            >
                <IconRefresh size={13} /> {t('worldCupJersey.reset')}
            </button>
        </div>
    );

    const filterBody = (
        <>
            {/* Category — scrolls on the Y axis once there are more than 10 rows */}
            <FilterLabel>{t('worldCupJersey.category')}</FilterLabel>
            <div className={`flex flex-col gap-0.5 mb-5 ${categories.length + 1 > 10 ? 'max-h-[400px] overflow-y-auto pr-1 [scrollbar-width:thin]' : ''}`}>
                {loading ? (
                    <CategoryRowsSkeleton />
                ) : (
                    <>
                        <CategoryRow active={activeCategory === 'all'} label={t('worldCupJersey.all')} count={fmt(visibleUniverse.length)} onClick={() => handleCategoryChange('all')} />
                        {categories.map(c => (
                            <CategoryRow
                                key={c.slug}
                                active={activeCategory === c.slug}
                                label={getCatName(c)}
                                count={fmt(countByCategory.map.get(c.id) || 0)}
                                onClick={() => handleCategoryChange(c.slug)}
                            />
                        ))}
                    </>
                )}
            </div>

            {/* Pricing */}
            <FilterLabel>{t('printMedia.pricingLabel')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <RadioRow active={pricing === 'all'} title={t('printMedia.pricingAll')} onClick={() => setPricing('all')} />
                <RadioRow active={pricing === 'free'} title={t('printMedia.pricingFree')} hint={t('printMedia.pricingFreeHint')} onClick={() => setPricing('free')} />
                <RadioRow active={pricing === 'paid'} title={t('printMedia.pricingPaid')} hint={t('printMedia.pricingPaidHint')} onClick={() => setPricing('paid')} />
            </div>

            {/* Sort */}
            <FilterLabel>{t('worldCupJersey.sort')}</FilterLabel>
            <div className="flex flex-col gap-0.5">
                <SortRow active={sort === 'order'} icon={IconArrowsSort} label={t('printMedia.sortFeatured')} onClick={() => setSort('order')} />
                <SortRow active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                <SortRow active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                <SortRow active={sort === 'oldest'} icon={IconClock} label={t('worldCupJersey.sortOldest')} onClick={() => setSort('oldest')} />
                <SortRow active={sort === 'name'} icon={IconSortAscendingLetters} label={t('worldCupJersey.sortName')} onClick={() => setSort('name')} />
            </div>
        </>
    );

    const headerTitle = getCatName(activeCategoryObj) || t('navigation.printMedia');

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {headerTitle}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-300">
                        {t('printMedia.browserSubtitle')}
                    </p>
                </div>
                <div className="flex-shrink-0 flex items-center justify-end gap-2 flex-wrap">
                    {/* Quick one-click sort: the featured shelf / newest */}
                    <div className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <QuickSortPill active={sort === 'order'} icon={IconArrowsSort} label={t('printMedia.sortFeatured')} onClick={() => setSort('order')} />
                        <QuickSortPill active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                    </div>
                    {/* Direction toggle — flips the active sort between desc (default) and asc. */}
                    <button
                        onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
                        title={sortDir === 'desc' ? t('worldCupJersey.sortDesc') : t('worldCupJersey.sortAsc')}
                        aria-label={sortDir === 'desc' ? t('worldCupJersey.sortDesc') : t('worldCupJersey.sortAsc')}
                        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer"
                    >
                        {sortDir === 'desc'
                            ? <IconSortAscending size={16} className="text-[#E2702E]" />
                            : <IconSortDescending size={16} className="text-[#E2702E]" />}
                        <span className="hidden sm:inline">{sortDir === 'desc' ? t('worldCupJersey.sortToggleAsc') : t('worldCupJersey.sortToggleDesc')}</span>
                    </button>
                    {/* ❤️ Favorites-only toggle — just the templates the user hearted. */}
                    <button
                        onClick={() => setFavOnly((v) => !v)}
                        title={t('worldCupJersey.favoritesSection')}
                        aria-pressed={favOnly}
                        className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-[11px] border text-[13px] font-semibold transition-colors cursor-pointer ${favOnly
                            ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                            : 'border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'}`}
                    >
                        {favOnly ? <IconHeartFilled size={16} className="text-red-500" /> : <IconHeart size={16} className="text-[#E2702E]" />}
                        <span className="hidden sm:inline">{t('worldCupJersey.favoritesSection')}</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-5 items-start">

                {/* ───────────────── Sidebar (desktop, lg and up) ───────────────── */}
                <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-4 self-start bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl p-4">
                    {filterHeaderRow}
                    {filterBody}
                </aside>

                {/* ─────────────────────────── Main ─────────────────────────── */}
                <main ref={gridRef} className="flex-1 min-w-0 w-full">
                    {/* Search + mobile filter trigger */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2.5 h-12 px-3.5 flex-1 min-w-0 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('printMedia.searchPlaceholder')}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    aria-label={t('worldCupJersey.clearSearch')}
                                    className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#E2702E] hover:bg-[#F1EFEA] dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        {/* Only shown below lg — opens the filter drawer */}
                        <button
                            onClick={() => setFilterSheetOpen(true)}
                            className="lg:hidden relative flex-shrink-0 inline-flex items-center gap-1.5 h-12 px-4 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors"
                        >
                            <IconAdjustmentsHorizontal size={18} className="text-[#E2702E]" />
                            <span className="hidden sm:inline">{t('worldCupJersey.filters')}</span>
                            {filtersDirty && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#E2702E]" />}
                        </button>
                    </div>

                    {/* Category marquee + result count + sort */}
                    <div className="flex items-center gap-3 mb-4">
                        {categories.length > 0 ? (
                            <div
                                className="flex-1 min-w-0 relative overflow-hidden"
                                onMouseEnter={() => setMarqueePaused(true)}
                                onMouseLeave={() => setMarqueePaused(false)}
                                style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)' }}
                            >
                                <div
                                    ref={marqueeRowRef}
                                    className="flex gap-2 w-max py-0.5"
                                    style={{
                                        animationName: 'pmb-cat-marquee',
                                        animationDuration: `${marqueeDuration}s`,
                                        animationTimingFunction: 'linear',
                                        animationIterationCount: 'infinite',
                                        animationPlayState: marqueePaused ? 'paused' : 'running',
                                    }}
                                >
                                    {[...categories, ...categories].map((cat, i) => {
                                        const active = activeCategory === cat.slug;
                                        return (
                                            <button
                                                key={`${cat.slug}-${i}`}
                                                onClick={() => handleCategoryChange(active ? 'all' : cat.slug)}
                                                className={`flex-shrink-0 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${active
                                                    ? 'bg-[#E2702E] border-[#E2702E] text-white'
                                                    : 'text-[#6E6A63] dark:text-zinc-300 bg-white dark:bg-zinc-800 border-[#E9E6E0] dark:border-zinc-700 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'}`}
                                            >
                                                {getCatName(cat)}
                                            </button>
                                        );
                                    })}
                                </div>
                                <style>{`@keyframes pmb-cat-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
                            </div>
                        ) : <div className="flex-1" />}
                        <div className="flex-shrink-0 flex items-center gap-2.5">
                            <p className="text-[13px] text-[#6E6A63] dark:text-zinc-300 whitespace-nowrap hidden sm:block">
                                {fmt(ordered.length)} {t('worldCupJersey.templatesFound')}
                            </p>
                            <SortMenu sort={sort} setSort={setSort} dateRange={dateRange} setDateRange={setDateRange} />
                        </div>
                    </div>

                    {/* Skeleton */}
                    {loading && <SkeletonMasonry />}

                    {/* Empty */}
                    {!loading && ordered.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                                {favOnly
                                    ? <IconHeart size={28} className="text-[#C3BEB4] dark:text-zinc-500" />
                                    : <IconPrinter size={28} className="text-[#C3BEB4] dark:text-zinc-500" />}
                            </div>
                            <p className="text-sm text-[#6E6A63] dark:text-zinc-300">
                                {favOnly
                                    ? t('worldCupJersey.favoritesEmpty')
                                    : filtersDirty ? t('printMedia.noMatch') : t('printMedia.noTemplates')}
                            </p>
                            {filtersDirty && (
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    className="px-4 py-2 rounded-[10px] border border-[#E9E6E0] dark:border-zinc-700 text-sm font-semibold text-[#6E6A63] dark:text-zinc-300"
                                >
                                    {t('printMedia.clearFilters')}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Grid */}
                    {!loading && ordered.length > 0 && (
                        <>
                            <BalancedMasonry
                                items={shown}
                                columnCount={columnCount}
                                heightOf={heightOf}
                                // Under the featured sort the grid IS the admin's
                                // arrangement, and balanced packing would scramble
                                // the reading order it was arranged in.
                                sequential={sort === 'order'}
                                renderItem={(tpl) => (
                                    <PrintTemplateCard
                                        key={tpl.id}
                                        tpl={tpl}
                                        isBn={isBn}
                                        onMeasure={reportRatio}
                                        onClick={() => navigate(`/print-media/editor/${tpl.slug || tpl.id}`)}
                                        isFavorite={favSet.has(tpl.id)}
                                        onToggleFavorite={() => toggleFavorite(tpl.id)}
                                    />
                                )}
                            />

                            {hasMore && (
                                <div ref={sentinelRef} className="flex justify-center items-center gap-2 mt-6 py-4 text-[#E2702E]">
                                    <IconLoader2 size={20} className="animate-spin" />
                                    <span className="text-sm font-medium">{t('common.loading')}</span>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Feedback prompt — rises from the bottom once there is nothing left
                to scroll. Kept mounted so it can animate both ways, and made
                click-through while hidden so it never eats a tap on the grid. */}
            <div
                className={`fixed inset-x-0 bottom-[88px] nav:bottom-6 z-[80] flex justify-center px-4 transition-all duration-300 ease-out ${
                    pillVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none'
                }`}
            >
                {/* The shake lives here, not on the wrapper above — that one owns
                    the slide-in translate, and two transforms on one element
                    fight each other. */}
                <style>{`
                    @keyframes skj-pill-shake {
                        0%, 100% { transform: translateX(0); }
                        15%      { transform: translateX(-5px); }
                        30%      { transform: translateX(5px); }
                        45%      { transform: translateX(-4px); }
                        60%      { transform: translateX(4px); }
                        80%      { transform: translateX(-2px); }
                    }
                `}</style>
                <div
                    onAnimationEnd={() => setPillShaking(false)}
                    style={pillShaking ? { animation: 'skj-pill-shake 600ms ease-in-out' } : undefined}
                    className="flex items-center gap-3 max-w-[min(560px,100%)] rounded-full bg-[#E2702E] shadow-lg shadow-black/20 pl-4 pr-2 py-2"
                >
                    <span className="hidden sm:flex flex-shrink-0 w-9 h-9 rounded-full bg-white/20 items-center justify-center">
                        <IconMessage2 size={18} className="text-white" />
                    </span>
                    <p className="min-w-0 flex-1 text-[12.5px] sm:text-[13px] leading-snug text-white">
                        {t('printFeedback.pillMessage')}
                    </p>
                    <button
                        type="button"
                        onClick={() => setFeedbackOpen(true)}
                        className="flex-shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#1C1B19] hover:bg-black text-white text-[12.5px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                        <IconMessage2 size={15} className="sm:hidden" />
                        <span className="hidden sm:inline">{t('printFeedback.pillAction')}</span>
                        <span className="sm:hidden">{t('printFeedback.buttonLabel')}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setPillDismissed(true)}
                        aria-label={t('printFeedback.pillDismiss')}
                        title={t('printFeedback.pillDismiss')}
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/75 hover:text-white hover:bg-white/20 transition-colors cursor-pointer"
                    >
                        <IconX size={15} />
                    </button>
                </div>
            </div>

            <PrintFeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

            {/* ───────────── Filter drawer (below lg) ───────────── */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetContent side="left" className="w-[86%] max-w-xs overflow-y-auto p-4 font-hind">
                    <SheetTitle className="sr-only">{t('worldCupJersey.filters')}</SheetTitle>
                    <div className="pr-7">{filterHeaderRow}</div>
                    {filterBody}
                </SheetContent>
            </Sheet>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sidebar primitives
// ---------------------------------------------------------------------------
function FilterLabel({ children }) {
    return <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400 mb-2">{children}</p>;
}

function CategoryRowsSkeleton({ rows = 6 }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg animate-pulse">
                    <span className="h-3 rounded bg-zinc-200 dark:bg-zinc-700" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
                    <span className="ml-2 flex-shrink-0 h-4 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                </div>
            ))}
        </>
    );
}

function CategoryRow({ active, label, count, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active
                    ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30'
                    : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className="truncate text-left">{label}</span>
            <span className={`ml-2 flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                active ? 'text-[#E2702E] bg-white dark:bg-zinc-900' : 'text-[#A8A294] dark:text-zinc-400 bg-[#F1EFEA] dark:bg-zinc-800'
            }`}>{count}</span>
        </button>
    );
}

function RadioRow({ active, title, hint, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                active ? 'bg-[#FCEFE7] dark:bg-orange-950/30' : 'hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className={`w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-colors ${
                active ? 'border-[4.5px] border-[#E2702E]' : 'border-[#C3BEB4] dark:border-zinc-600'
            }`} />
            <span className="flex flex-col leading-tight">
                <span className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-100">{title}</span>
                {hint && <span className="text-[10.5px] text-[#9C988F] dark:text-zinc-400">{hint}</span>}
            </span>
        </button>
    );
}

// Compact segmented pill for the top-right one-click sort toggles.
function QuickSortPill({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[9px] text-[13px] font-semibold transition-colors cursor-pointer ${
                active
                    ? 'bg-[#E2702E] text-white'
                    : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            <Icon size={15} className={active ? 'text-white' : 'text-[#E2702E]'} />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}

function SortRow({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30' : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <Icon size={14} />
            {label}
        </button>
    );
}

// ---------------------------------------------------------------------------
// Top-right Sort / date-range dropdown (mirrors the sidebar sort + adds a
// "created within" date filter: today, last 3 / 7 / 30 days).
// ---------------------------------------------------------------------------
function SortMenu({ sort, setSort, dateRange, setDateRange }) {
    const { t } = useTranslation();
    const sortOptions = [
        { value: 'order', label: t('printMedia.sortFeatured') },
        { value: 'popular', label: t('worldCupJersey.sortPopular') },
        { value: 'newest', label: t('worldCupJersey.sortNewest') },
        { value: 'oldest', label: t('worldCupJersey.sortOldest') },
        { value: 'name', label: t('worldCupJersey.sortName') },
    ];
    const dateOptions = [
        { value: 'all', label: t('worldCupJersey.dateAll') },
        { value: 'today', label: t('worldCupJersey.dateToday') },
        { value: '3days', label: t('worldCupJersey.date3Days') },
        { value: '7days', label: t('worldCupJersey.date7Days') },
        { value: '30days', label: t('worldCupJersey.date30Days') },
    ];
    const active = sort !== 'order' || dateRange !== 'all';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer">
                    <IconArrowsSort size={16} className="text-[#E2702E]" />
                    <span className="hidden sm:inline">{t('worldCupJersey.sortAction')}</span>
                    <IconChevronDown size={14} className="text-[#9C988F] dark:text-zinc-400" />
                    {active && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#E2702E]" />}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 font-hind">
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconArrowsSort size={13} /> {t('worldCupJersey.sortBy')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sort} onValueChange={setSort}>
                    {sortOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">
                            {o.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconCalendar size={13} /> {t('worldCupJersey.dateRange')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={dateRange} onValueChange={setDateRange}>
                    {dateOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">
                            {o.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------------------
// Template card — natural-aspect thumbnail so masonry packs without gaps.
// ---------------------------------------------------------------------------
/** "1.2k" rather than "1247" — the badge is a glance, not a figure. */
function formatUses(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

function PrintTemplateCard({ tpl, isBn, onClick, onMeasure, isFavorite = false, onToggleFavorite }) {
    const { t } = useTranslation();
    const title = (isBn && tpl.titleBn) ? tpl.titleBn : tpl.title;
    const cat = tpl.categoryRef;
    const catName = isBn ? (cat?.nameBn || cat?.name) : (cat?.name || cat?.nameBn);
    const [loaded, setLoaded] = useState(false);
    // Ratio (height/width) of the thumbnail image itself. A thumbnail is often
    // cropped to its own ratio rather than the artboard's, so once it loads the
    // card follows the image — the admin list has done this for a while, and
    // without it a wide card in a tall frame was blown up and cut in half.
    const [thumbRatio, setThumbRatio] = useState(null);
    const w = Number(tpl.width) || 1050;
    const h = Number(tpl.height) || 600;
    // Page size is only the pre-load estimate; the loaded thumbnail wins.
    const frameAspect = thumbRatio ? `1 / ${thumbRatio}` : `${w} / ${h}`;

    return (
        <div
            onClick={onClick}
            className="group mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 cursor-pointer relative transition-all duration-150 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#E2702E]/40"
        >
            <div className="relative">
                {tpl.thumbnail ? (
                    // Reserve the thumbnail's height up-front from the template's own
                    // page size, so the card never collapses then reflows on load.
                    <div
                        className="relative w-full overflow-hidden bg-[#F0EFEC] dark:bg-zinc-800"
                        style={{ aspectRatio: frameAspect }}
                    >
                        {!loaded && <div className="absolute inset-0 animate-pulse bg-[#E9E7E2] dark:bg-zinc-700/60" />}
                        <img
                            src={tpl.thumbnail}
                            alt={title}
                            loading="lazy"
                            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={(e) => {
                                const { naturalWidth, naturalHeight } = e.currentTarget;
                                if (naturalWidth) {
                                    const r = naturalHeight / naturalWidth;
                                    setThumbRatio(r);
                                    onMeasure?.(tpl.id, r);
                                }
                                setLoaded(true);
                            }}
                            onError={() => setLoaded(true)}
                        />
                    </div>
                ) : (
                    <div
                        className="w-full flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800"
                        style={{ aspectRatio: frameAspect }}
                    >
                        <IconPrinter size={28} className="text-[#C3BEB4] dark:text-zinc-500" />
                    </div>
                )}

                {/* Uses and the heart. The heart's WIDTH is collapsed until
                    hover — opacity alone would still reserve the space, so the
                    badge would sit oddly far from the edge at rest. A favourited
                    heart stays expanded. */}
                <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5">
                    {tpl.usageCount > 0 && (
                        <span className="text-[10.5px] font-semibold text-white bg-black/55 backdrop-blur-sm px-2.5 py-[3px] rounded-full whitespace-nowrap">
                            {formatUses(tpl.usageCount)} {t('worldCupJersey.uses')}
                        </span>
                    )}
                    {onToggleFavorite && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                            title={isFavorite ? t('worldCupJersey.removeFavorite') : t('worldCupJersey.addFavorite')}
                            className={`h-7 rounded-full backdrop-blur-sm flex items-center justify-center flex-shrink-0 overflow-hidden transition-all duration-200 ease-out cursor-pointer ${
                                isFavorite
                                    ? 'w-7 opacity-100 bg-white/90 text-red-500'
                                    : 'w-0 opacity-0 group-hover:w-7 group-hover:opacity-100 focus-visible:w-7 focus-visible:opacity-100 bg-black/40 text-white hover:bg-black/60'
                            }`}
                        >
                            <span className="flex-shrink-0 flex items-center justify-center">
                                {isFavorite ? <IconHeartFilled size={15} /> : <IconHeart size={15} />}
                            </span>
                        </button>
                    )}
                </div>

                {/* Free badge — this template costs no credit to export */}
                {tpl.isFree && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="absolute top-2.5 left-2.5 z-10 text-[10.5px] font-bold text-white bg-emerald-500 px-2.5 py-[3px] rounded-full shadow-sm cursor-help">
                                {t('profile.creditFree')}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t('profile.freeTemplateTooltip')}</TooltipContent>
                    </Tooltip>
                )}

                {/* Hover overlay: centered CTA + name/category pinned to bottom */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 via-black/10 to-black/25">
                    {/* Centered CTA */}
                    <div className="absolute inset-0 flex items-center justify-center p-3.5">
                        <div className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#E2702E] text-white text-[12.5px] font-semibold">
                            <IconArrowUp size={14} />
                            {t('printMedia.openTemplate')}
                        </div>
                    </div>

                    {/* Name & category — absolute bottom, transparent bg */}
                    <div className="absolute inset-x-0 bottom-0 px-3.5 py-2.5">
                        <p className="text-[13px] font-semibold text-white truncate">{title}</p>
                        {catName && <p className="text-[11px] text-white/70 mt-0.5 truncate">{catName}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Skeleton masonry — staggered heights to mimic the real grid
// ---------------------------------------------------------------------------
function SkeletonMasonry() {
    const heights = ['h-[260px]', 'h-[200px]', 'h-[300px]', 'h-[230px]', 'h-[280px]', 'h-[210px]', 'h-[320px]', 'h-[240px]', 'h-[260px]'];
    return (
        <Masonry breakpointCols={masonryBreakpoints} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
            {heights.map((h, i) => (
                <div key={i} className={`mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse ${h}`} />
            ))}
        </Masonry>
    );
}
